import { Check, Close, Tune } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  MobileStepper,
  Popover,
  Select,
  Slider,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import axios from "axios";
import { User, getStripeForAccount } from "../../utils";
import { useEffect, useMemo, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import PaymentRedirectHandler from "../../components/PaymentRedirectHandler";
import {
  SubscriptionTierCard,
  SubscriptionTierCardSkeleton,
  TierData,
} from "../../components/SubscriptionTierCard";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { hostname, parseError, isLocal } from "../../utils";
import PaymentStep from "./PaymentStep";
import ServicePackagesStep, { ServicePackage } from "./ServicePackagesStep";
import { SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION } from "../../constants";
import capture from "../../capture";

interface SubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  currentUser: User;
  getUsage: () => Promise<void>;
  token?: string;
  getToken: () => Promise<string>;
  eventListener: Function;
  mode: string;
  currentPlan: string;
  hasExistingSubscription?: boolean;
  currentSubscriptionPlan?: string;
  currentSubscriptionConfig?: {
    seats?: number;
    companies?: number;
    creditPackages?: number;
  };
  onSubscriptionUpdate?: () => void;
  skipReloadAfterPayment?: boolean;
  /** When true, user cannot close the dialog without subscribing (free tier disabled) */
  requiresSubscription?: boolean;
  /** Optional logout handler for /subscribe page */
  onLogout?: () => void;
}

const SubscriptionDialog: React.FC<SubscriptionDialogProps> = ({
  open,
  onClose,
  currentUser,
  getUsage,
  getToken,
  eventListener,
  mode,
  currentPlan,
  hasExistingSubscription = false,
  currentSubscriptionPlan,
  currentSubscriptionConfig,
  onSubscriptionUpdate,
  skipReloadAfterPayment = false,
  requiresSubscription = false,
  onLogout,
}) => {
  const [planSelection, setPlanSelection] = useState(
    hasExistingSubscription && currentSubscriptionPlan
      ? currentSubscriptionPlan
      : currentPlan
  );
  const [billingPeriod, setBillingPeriod] = useState<"annual" | "monthly">(
    "annual"
  );
  const [activeStep, setActiveStep] = useState(0);
  // Per-tier configuration dictionary
  const [tierConfigs, setTierConfigs] = useState<
    Record<
      string,
      {
        seats?: number;
        companies?: number;
        creditPackages?: number;
      }
    >
  >({});
  const [clientSecret, setClientSecret] = useState("");
  const [stepLoading, setStepLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creditLevel, setCreditLevel] = useState("standard");
  const [showRedirectHandler, setShowRedirectHandler] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeData, setPromoCodeData] = useState<any>(null);
  const [promoCodeError, setPromoCodeError] = useState("");
  const [validatingPromoCode, setValidatingPromoCode] = useState(false);
  const [configAnchorEl, setConfigAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [configTierId, setConfigTierId] = useState<string | null>(null);
  const [downgradeConfirmOpen, setDowngradeConfirmOpen] = useState(false);
  const [pendingDowngradePlan, setPendingDowngradePlan] = useState<
    string | null
  >(null);
  const [initialTierConfigs, setInitialTierConfigs] = useState<
    Record<
      string,
      {
        seats?: number;
        companies?: number;
        creditPackages?: number;
      }
    >
  >({});

  // Get whitelabel settings for custom pricing
  const { whitelabelSettings } = useWhitelabel();

  // Responsive breakpoints for tier table chunking
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // 1 per row
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md")); // 2 per row
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg")); // 3 per row
  // lg and up = 4 per row

  // Get number of tiers per row based on screen size
  const getTiersPerRow = () => {
    if (isXs) return 1;
    if (isSm) return 2;
    if (isMd) return 3;
    return 4;
  };
  const [pricingLoading, setPricingLoading] = useState(false);
  const [calculatedPricing, setCalculatedPricing] = useState<any>({});
  const [confirmedPricing, setConfirmedPricing] = useState<any>(null);
  const [confirmedFirstMonthSpecial, setConfirmedFirstMonthSpecial] = useState<{
    applied: boolean;
    price: number;
    regularPrice: number;
    label: string;
  } | null>(null);
  const [tierData, setTierData] = useState<any>(null);
  const [tierDataLoading, setTierDataLoading] = useState(false);

  // Service packages state
  const [selectedServicePackages, setSelectedServicePackages] = useState<
    string[]
  >([]);
  const [availableServicePackages, setAvailableServicePackages] = useState<
    ServicePackage[]
  >([]);
  const [hasServicePackages, setHasServicePackages] = useState(false);
  const [servicePackagesChecked, setServicePackagesChecked] = useState(false);

  // Connected account for direct charges (whitelabel)
  const [connectedAccount, setConnectedAccount] = useState<string | undefined>(undefined);

  // Get the appropriate Stripe instance (platform or connected account for direct charges)
  const stripeInstance = useMemo(() => {
    return getStripeForAccount(connectedAccount);
  }, [connectedAccount]);

  // Check if we're on a local development domain or main platform (via whitelabel settings)
  const isMainDomain = isLocal || whitelabelSettings?.isMainSite === true;

  // Check for payment redirect when dialog opens
  useEffect(() => {
    if (open) {
      capture("Subscribe Open", {});
      // Reset connected account state when dialog opens
      setConnectedAccount(undefined);

      const urlParams = new URLSearchParams(window.location.search);
      const paymentRedirect = urlParams.get("payment_redirect");
      const paymentIntentClientSecret = urlParams.get(
        "payment_intent_client_secret"
      );

      if (paymentRedirect === "true" && paymentIntentClientSecret) {
        setShowRedirectHandler(true);
        // Payment redirect - set a high step number that will be rendered as payment
        // The rendering logic handles this by checking activeStep > 0 and not service packages step
        setActiveStep(99); // Special value for payment redirect

        // Also restore the plan selection from URL params
        const productPlan = urlParams.get("product_plan");
        const billingPeriod = urlParams.get("billing_period");
        const creditLevel = urlParams.get("credit_level");
        const seats = urlParams.get("seats");

        if (productPlan) setPlanSelection(productPlan);
        if (billingPeriod === "annual" || billingPeriod === "monthly")
          setBillingPeriod(billingPeriod);
        if (creditLevel) setCreditLevel(creditLevel);
        if (seats && productPlan) {
          setTierConfigs((prev) => ({
            ...prev,
            [productPlan]: {
              ...prev[productPlan],
              seats: parseInt(seats),
            },
          }));
        }
      }
    }
  }, [open]);

  // Load tier data from backend endpoint
  useEffect(() => {
    const loadTierData = async () => {
      try {
        setTierDataLoading(true);
        const currentDomain = window.location.hostname;
        const response = await axios.get(`${hostname}/subscriptions/tiers`, {
          params: { domain: currentDomain },
        });

        let tierDataFromBackend = response.data;

        // Remove firstMonthSpecial if user already has a paid subscription
        // (only show special pricing for new subscribers or users on free plan)
        if (hasExistingSubscription && currentPlan !== "free") {
          tierDataFromBackend = {
            ...tierDataFromBackend,
            tiers: tierDataFromBackend.tiers?.map((tier: any) => ({
              ...tier,
              firstMonthSpecial: null,
            })),
          };
        }

        setTierData(tierDataFromBackend);
      } catch (error) {
        console.error("Error loading tier data from backend:", error);
        // Will fall back to frontend defaults if needed
        setTierData(null);
      } finally {
        setTierDataLoading(false);
      }
    };

    if (open) {
      loadTierData();
    }
  }, [open, hasExistingSubscription, currentPlan]);

  // Check if service packages are available (independent of plan selection)
  useEffect(() => {
    const checkServicePackages = async () => {
      try {
        const response = await axios.get(`${hostname}/service-packages`, {
          params: { domain: window.location.hostname },
        });

        if (response.data.success && response.data.packages?.length > 0) {
          setHasServicePackages(true);
          setAvailableServicePackages(response.data.packages);
        } else {
          setHasServicePackages(false);
          setAvailableServicePackages([]);
        }
      } catch (error) {
        console.error("Error checking service packages:", error);
        setHasServicePackages(false);
        setAvailableServicePackages([]);
      } finally {
        setServicePackagesChecked(true);
      }
    };

    if (open) {
      checkServicePackages();
    }
  }, [open]);

  // Validate promo code
  const validatePromoCode = async (code: string) => {
    if (!code.trim()) {
      setPromoCodeData(null);
      setPromoCodeError("");
      return;
    }

    try {
      setValidatingPromoCode(true);
      setPromoCodeError("");

      const tierConfig = tierConfigs[planSelection] || {};
      const seatCount = tierConfig.seats || 1;

      const response = await axios.get(
        `${hostname}/subscriptions/validate-promo-code/${encodeURIComponent(
          code
        )}`,
        {
          params: {
            plan: planSelection,
            seats: seatCount,
            domain: window.location.hostname,
          },
        }
      );

      if (response.data.valid) {
        setPromoCodeData(response.data);
        setPromoCodeError("");
      } else {
        setPromoCodeData(null);
        setPromoCodeError(response.data.error || "Invalid promo code");
      }
    } catch (error) {
      setPromoCodeData(null);
      setPromoCodeError("Error validating promo code");
    } finally {
      setValidatingPromoCode(false);
    }
  };

  // Debounce promo code validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (promoCode) {
        validatePromoCode(promoCode);
      } else {
        setPromoCodeData(null);
        setPromoCodeError("");
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [promoCode, planSelection, tierConfigs]);

  // Recalculate pricing when parameters change (with debouncing)
  useEffect(() => {
    if (!open || !tierData?.tiers) return;

    // Debounce pricing calculations to avoid multiple API calls
    const timer = setTimeout(async () => {
      const newPricing: any = {};

      // Calculate pricing for all tiers that need backend pricing
      const tiersNeedingPricing = tierData.tiers.filter(
        (tier: any) => tier.id && tier.priceFromBackend && !tier.isContactSales
      );

      for (const tier of tiersNeedingPricing) {
        // Determine parameters based on tier configuration
        const creditLvl = tier.id === "entrepreneur" ? creditLevel : "standard";
        const tierConfig = tierConfigs[tier.id] || {};
        const seatCount = tier.hasConfiguration ? tierConfig.seats || 1 : 1;
        const companyCount = tier.hasConfiguration
          ? tierConfig.companies || 0
          : 0;
        const creditPkgs = tier.hasConfiguration
          ? tierConfig.creditPackages || 0
          : 0;
        const promoToUse = promoCodeData?.valid ? promoCode : undefined;

        const pricing = await calculatePricingWithPromo(
          tier.id,
          billingPeriod,
          seatCount,
          creditLvl,
          promoToUse,
          companyCount,
          creditPkgs
        );
        if (pricing) {
          newPricing[`${tier.id}_${billingPeriod}_${creditLvl}_${seatCount}`] =
            pricing;
        }
      }

      setCalculatedPricing(newPricing);
    }, 300); // 300ms debounce - waits for user to stop changing options

    return () => clearTimeout(timer);
  }, [billingPeriod, tierConfigs, creditLevel, promoCodeData, open, tierData]);

  // Calculate pricing with promo code from backend
  const calculatePricingWithPromo = async (
    plan: string,
    period: string,
    seatCount: number,
    creditLvl: string,
    promoCode?: string,
    companyCount?: number,
    creditPkgs?: number
  ) => {
    if (!plan || !period) {
      return null;
    }
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/subscriptions/calculate-pricing`,
        {
          productPlan: plan,
          billingPeriod: period,
          seats: seatCount,
          creditLevel: creditLvl,
          promoCode: promoCode,
          companies: companyCount,
          creditPackages: creditPkgs,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          params: {
            domain: window.location.hostname,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error calculating pricing:", error);
      return null;
    }
  };

  // Helper to get pricing from calculated pricing state
  const getPricing = (
    plan: string,
    period: string,
    creditLvl: string,
    seatCount: number
  ) => {
    const key = `${plan}_${period}_${creditLvl}_${seatCount}`;
    return calculatedPricing[key];
  };

  // Determine if user is downgrading to free plan
  const isDowngrading =
    hasExistingSubscription &&
    currentSubscriptionPlan &&
    currentSubscriptionPlan !== "free" &&
    planSelection === "free";

  // Check if configuration has changed for the current plan
  const hasConfigurationChanged = () => {
    if (!hasExistingSubscription) return false;
    if (planSelection !== currentSubscriptionPlan) return false; // Plan changed, handle separately

    const currentConfig = tierConfigs[planSelection] || {};
    const initialConfig = initialTierConfigs[planSelection] || {};

    return (
      currentConfig.seats !== initialConfig.seats ||
      currentConfig.companies !== initialConfig.companies ||
      currentConfig.creditPackages !== initialConfig.creditPackages
    );
  };

  // Determine if user is updating their subscription (not downgrading to free)
  const isUpdatingSubscription =
    hasExistingSubscription &&
    !isDowngrading &&
    (planSelection !== currentSubscriptionPlan || hasConfigurationChanged());

  // Handle downgrade to free plan
  const handleDowngradeToFree = async () => {
    try {
      setStepLoading(true);
      const currentToken = await getToken();

      const response = await axios.post(
        `${hostname}/subscriptions/cancel`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      // Refresh usage and close dialog
      console.log(
        "[SubscriptionDialog] Subscription cancelled - calling callbacks"
      );
      await getUsage();
      if (onSubscriptionUpdate) {
        console.log("[SubscriptionDialog] Calling onSubscriptionUpdate");
        onSubscriptionUpdate();
      }
      console.log("[SubscriptionDialog] Calling onClose");
      onClose();

      eventListener({
        type: "SET_SUCCESS",
        message:
          "Subscription cancelled. You've been downgraded to the free plan.",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setStepLoading(false);
      setDowngradeConfirmOpen(false);
    }
  };

  // Handle subscription update (for plan changes other than downgrade)
  const handleUpdateSubscription = async () => {
    try {
      setStepLoading(true);
      const currentToken = await getToken();

      const selectedTierConfig = tierConfigs[planSelection] || {};

      const updatePayload: any = {
        productPlan: planSelection,
        seats: selectedTierConfig.seats || 1,
        billingPeriod: billingPeriod,
        creditLevel: creditLevel || "standard",
      };

      if (
        selectedTierConfig.companies !== undefined &&
        selectedTierConfig.companies !== null
      ) {
        updatePayload.companies = selectedTierConfig.companies;
      }
      if (
        selectedTierConfig.creditPackages !== undefined &&
        selectedTierConfig.creditPackages !== null
      ) {
        updatePayload.creditPackages = selectedTierConfig.creditPackages;
      }
      if (promoCodeData?.valid && promoCode) {
        updatePayload.promoCode = promoCode;
      }

      const response = await axios.post(
        `${hostname}/subscriptions/update`,
        updatePayload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (response.data.pricing) {
        setConfirmedPricing(response.data.pricing);
      }

      // Store first month special info if applied
      if (response.data.firstMonthSpecial) {
        setConfirmedFirstMonthSpecial(response.data.firstMonthSpecial);
      } else {
        setConfirmedFirstMonthSpecial(null);
      }

      // Store connected account for direct charges (whitelabel)
      if (response.data.connectedAccount) {
        setConnectedAccount(response.data.connectedAccount);
      }

      // Check if payment was already processed (no clientSecret needed)
      if (!response.data.clientSecret) {
        // Payment was processed automatically
        console.log(
          "[SubscriptionDialog] Payment processed automatically - calling callbacks"
        );
        setStepLoading(false);
        await getUsage();
        if (onSubscriptionUpdate) {
          console.log(
            "[SubscriptionDialog] Calling onSubscriptionUpdate after automatic payment"
          );
          onSubscriptionUpdate();
        }
        console.log(
          "[SubscriptionDialog] Calling onClose after automatic payment"
        );
        onClose();

        eventListener({
          type: "SET_SUCCESS",
          message: "Subscription updated successfully!",
        });
      } else {
        // Payment confirmation needed
        setClientSecret(response.data.clientSecret);
        setStepLoading(false);
        setActiveStep(1);
      }
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      setStepLoading(false);
    }
  };

  const getSetupIntent = async () => {
    try {
      setStepLoading(true);
      const currentToken = await getToken();

      // Get config for the selected plan
      const selectedTierConfig = tierConfigs[planSelection] || {};

      console.log("Selected tier config:", planSelection, selectedTierConfig);

      // Build the subscription creation payload
      const createPayload: any = {
        name: currentUser.displayName,
        email: currentUser.email,
        seats: selectedTierConfig.seats || 1,
        billingPeriod: billingPeriod,
        productPlan: planSelection,
        creditLevel: creditLevel || "standard",
        domain: window.location.hostname,
      };

      // Only add optional fields if they're defined
      if (
        selectedTierConfig.companies !== undefined &&
        selectedTierConfig.companies !== null
      ) {
        createPayload.companies = selectedTierConfig.companies;
      }
      if (
        selectedTierConfig.creditPackages !== undefined &&
        selectedTierConfig.creditPackages !== null
      ) {
        createPayload.creditPackages = selectedTierConfig.creditPackages;
      }
      if (promoCodeData?.valid && promoCode) {
        createPayload.promoCode = promoCode;
      }

      console.log("Creating subscription with payload:", createPayload);

      // Backend calculates pricing, no need to send price from frontend
      const response = await axios.post(
        `${hostname}/subscriptions/create`,
        createPayload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      setClientSecret(response.data.clientSecret);
      // Store the pricing information returned from the backend
      if (response.data.pricing) {
        setConfirmedPricing(response.data.pricing);
      }
      // Store first month special info if applied
      if (response.data.firstMonthSpecial) {
        setConfirmedFirstMonthSpecial(response.data.firstMonthSpecial);
      } else {
        setConfirmedFirstMonthSpecial(null);
      }
      // Store connected account for direct charges (whitelabel)
      if (response.data.connectedAccount) {
        setConnectedAccount(response.data.connectedAccount);
      }
      setStepLoading(false);
      // If service packages exist and we're coming from step 1, go to step 2
      // Otherwise go to step 1 (payment when no service packages)
      setActiveStep(servicePackagesChecked && hasServicePackages ? 2 : 1);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      setStepLoading(false);
    }
  };

  // Initialize configuration values when tier data is loaded
  useEffect(() => {
    if (tierData?.tiers) {
      const initialConfigs: Record<string, any> = {};
      tierData.tiers.forEach((tier: any) => {
        if (tier.hasConfiguration && tier.configuration) {
          const config = tier.configuration;

          // If this is the current subscription plan and we have current config, use those values
          const isCurrent =
            hasExistingSubscription && tier.id === currentSubscriptionPlan;

          initialConfigs[tier.id] = {
            seats:
              isCurrent && currentSubscriptionConfig?.seats !== undefined
                ? currentSubscriptionConfig.seats
                : config.seats
                ? config.seats.default || config.seats.min
                : undefined,
            companies:
              isCurrent && currentSubscriptionConfig?.companies !== undefined
                ? currentSubscriptionConfig.companies
                : config.companies
                ? config.companies.default || config.companies.min
                : undefined,
            creditPackages:
              isCurrent &&
              currentSubscriptionConfig?.creditPackages !== undefined
                ? currentSubscriptionConfig.creditPackages
                : config.creditPackages
                ? 0
                : undefined,
          };
        }
      });
      setTierConfigs(initialConfigs);
      setInitialTierConfigs(initialConfigs); // Store initial config for comparison
    }
  }, [
    tierData,
    currentSubscriptionConfig,
    hasExistingSubscription,
    currentSubscriptionPlan,
  ]);

  // Companies and seats are independent
  // Users must explicitly add seats - companies are just for tracking

  useEffect(() => {
    console.log("Current Plan: ", currentPlan);
    if (currentPlan) {
      setPlanSelection(currentPlan);
    }
  }, [currentPlan]);

  // Calculate grid columns based on number of tiers
  const getGridColumns = () => {
    const tierCount = tierData?.tiers?.length || 0;
    if (tierCount === 1) return 12; // 1 tier: full width
    if (tierCount === 2) return 6; // 2 tiers: half width each
    if (tierCount === 3) return 4; // 3 tiers: third width each
    return 3; // 4+ tiers: quarter width each (max 4 per row)
  };

  // Handle opening configuration popover
  const handleOpenConfig = (
    event: React.MouseEvent<HTMLButtonElement>,
    tierId: string
  ) => {
    setConfigAnchorEl(event.currentTarget);
    setConfigTierId(tierId);
  };

  // Handle downgrade click
  const handleDowngradeClick = (tierId: string) => {
    setPendingDowngradePlan(tierId);
    setDowngradeConfirmOpen(true);
  };

  return (
    <>
      <Dialog
        open={downgradeConfirmOpen}
        onClose={() => setDowngradeConfirmOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>Confirm Downgrade</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
            Are you sure you want to downgrade to the free plan? You will lose
            access to all premium features immediately.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your current paid subscription will be cancelled, and you'll be able
            to use the free tier features.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDowngradeConfirmOpen(false)}
            variant="outlined"
          >
            Keep My Subscription
          </Button>
          <Button
            onClick={handleDowngradeToFree}
            variant="contained"
            color="error"
            disabled={stepLoading}
          >
            {stepLoading ? "Processing..." : "Yes, Downgrade"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={open}
        onClose={requiresSubscription ? undefined : onClose}
        fullScreen
        disableEscapeKeyDown={requiresSubscription}
      >
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {hasExistingSubscription ? "Update Subscription" : "Subscription"}
            </Typography>
            <Stepper
              activeStep={Math.min(activeStep, servicePackagesChecked && hasServicePackages ? 2 : 1)}
              sx={{ width: "60%", display: { xs: "none", md: "flex" } }}
            >
              <Step key="select-plan">
                <StepLabel>Select Plan</StepLabel>
              </Step>
              {servicePackagesChecked && hasServicePackages && (
                <Step key="service-packages">
                  <StepLabel>Add-ons</StepLabel>
                </Step>
              )}
              <Step key="payment">
                <StepLabel>Payment</StepLabel>
              </Step>
            </Stepper>
            <MobileStepper
              variant="dots"
              steps={servicePackagesChecked && hasServicePackages ? 3 : 2}
              position="static"
              activeStep={Math.min(activeStep, servicePackagesChecked && hasServicePackages ? 2 : 1)}
              sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}
              backButton={<div />}
              nextButton={<div />}
            />
            {requiresSubscription ? (
              <Box sx={{ width: 40 }} />
            ) : (
              <IconButton onClick={() => onClose()}>
                <Close />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {activeStep === 0 ? (
            <Box sx={{ py: 2 }}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sx={{ mb: 2, textAlign: "center" }}>
                  <ToggleButtonGroup
                    color="primary"
                    value={billingPeriod}
                    exclusive
                    onChange={(e: any) => setBillingPeriod(e.target.value)}
                    disabled={tierDataLoading}
                  >
                    <ToggleButton value="monthly">Monthly Billing</ToggleButton>
                    <ToggleButton value="annual">
                      Annual Billing (Save 20%)
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    <Link
                      href={whitelabelSettings?.pricingUrl || "/pricing"}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: "none" }}
                    >
                      View detailed pricing comparison →
                    </Link>
                  </Typography>
                </Grid>
                {/* Dynamically render all tiers in aligned table layout */}
                <Grid item xs={12}>
                  {tierDataLoading ? (
                    // Show skeleton loading cards while loading
                    <Grid container spacing={2}>
                      {[1, 2, 3, 4].map((i) => (
                        <Grid item xs={12} sm={6} md={4} lg={getGridColumns()} key={`skeleton-${i}`}>
                          <SubscriptionTierCardSkeleton />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    // Render tiers in table layout with responsive chunking
                    (() => {
                      const tiers = tierData?.tiers || [];
                      const tiersPerRow = getTiersPerRow();
                      const chunkedTiers: TierData[][] = [];
                      for (let i = 0; i < tiers.length; i += tiersPerRow) {
                        chunkedTiers.push(tiers.slice(i, i + tiersPerRow));
                      }

                      // Cell style matching Card variant="outlined" with p: 3
                      const cellStyle = {
                        border: "none",
                        p: 0,
                        verticalAlign: "top",
                      };

                      // Empty cell style for padding incomplete rows
                      const emptyCellStyle = {
                        border: "none",
                        p: 0,
                      };

                      // Helper to render empty cells for incomplete rows
                      const renderEmptyCells = (tierChunk: TierData[], rowIndex: number) => {
                        const emptyCount = tiersPerRow - tierChunk.length;
                        if (emptyCount <= 0) return null;
                        return Array.from({ length: emptyCount }, (_, i) => (
                          <TableCell key={`empty-${rowIndex}-${i}`} sx={emptyCellStyle} />
                        ));
                      };

                      return chunkedTiers.map((tierChunk, chunkIndex) => (
                        <Box
                          key={`tier-table-${chunkIndex}`}
                          sx={{
                            mb: "18px", // 18px between rows
                          }}
                        >
                          <Table
                            sx={{
                              tableLayout: "fixed",
                              borderCollapse: "separate",
                              borderSpacing: "18px 0", // Space between columns
                              width: "100%",
                            }}
                          >
                            <TableBody>
                              {/* Row 1: Tier Names */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => (
                                  <TableCell
                                    key={`name-${tier.id}`}
                                    sx={{
                                      ...cellStyle,
                                      borderTop: "1px solid",
                                      borderLeft: "1px solid",
                                      borderRight: "1px solid",
                                      borderColor: "divider",
                                      borderTopLeftRadius: 4,
                                      borderTopRightRadius: 4,
                                      p: 3,
                                      pb: 0,
                                    }}
                                  >
                                    <Typography variant="h5" gutterBottom>{tier.name}</Typography>
                                  </TableCell>
                                ))}
                                {renderEmptyCells(tierChunk, 1)}
                              </TableRow>
                              {/* Row 2: Descriptions */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => (
                                  <TableCell
                                    key={`desc-${tier.id}`}
                                    sx={{
                                      ...cellStyle,
                                      borderLeft: "1px solid",
                                      borderRight: "1px solid",
                                      borderColor: "divider",
                                      px: 3,
                                      py: 0,
                                    }}
                                  >
                                    <Tooltip title={tier.description} arrow>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          mb: 2,
                                          display: "-webkit-box",
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: "vertical",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          minHeight: "2.5em",
                                        }}
                                      >
                                        {tier.description}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                ))}
                                {renderEmptyCells(tierChunk, 2)}
                              </TableRow>
                              {/* Row 3: Prices */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => {
                                  const tierConfig = tierConfigs[tier.id] || {};
                                  const calculatedPricing = getPricing(
                                    tier.id,
                                    billingPeriod,
                                    creditLevel,
                                    tier.hasConfiguration ? tierConfig.seats || 1 : 1
                                  );
                                  // Check for first month special (only for monthly billing on main platform)
                                  const hasFirstMonthSpecial = !!tier.firstMonthSpecial;
                                  return (
                                    <TableCell
                                      key={`price-${tier.id}`}
                                      sx={{
                                        ...cellStyle,
                                        borderLeft: "1px solid",
                                        borderRight: "1px solid",
                                        borderColor: "divider",
                                        px: 3,
                                        py: 0,
                                        pb: 1,
                                      }}
                                    >
                                      {tier.isContactSales ? (
                                        <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
                                          <Typography variant="h6">{tier.priceLabel}</Typography>
                                        </Box>
                                      ) : tier.priceFromBackend ? (
                                        pricingLoading ? (
                                          <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
                                            <Typography variant="h3" sx={{ color: "text.secondary" }}>
                                              Loading...
                                            </Typography>
                                          </Box>
                                        ) : hasFirstMonthSpecial ? (
                                          // First month special pricing display
                                          <Box sx={{ mb: 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "baseline" }}>
                                              <Typography
                                                variant="h5"
                                                sx={{
                                                  textDecoration: "line-through",
                                                  color: "text.secondary",
                                                }}
                                              >
                                                {calculatedPricing
                                                  ? `$${calculatedPricing.pricePerUnit}`
                                                  : "..."}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "baseline" }}>
                                              <Typography variant="h3" color="primary.main">
                                                ${tier.firstMonthSpecial!.price}
                                              </Typography>
                                              <Typography variant="body1" sx={{ ml: 1 }} color="primary.main">
                                                {tier.firstMonthSpecial!.label}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        ) : (
                                          <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
                                            <Typography variant="h3">
                                              {calculatedPricing ? `$${calculatedPricing.pricePerUnit}` : "..."}
                                            </Typography>
                                            <Typography variant="body1" sx={{ ml: 1 }}>
                                              per month
                                            </Typography>
                                          </Box>
                                        )
                                      ) : (
                                        <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
                                          <Typography variant="h3">${tier.price ?? 0}</Typography>
                                          <Typography variant="body1" sx={{ ml: 1 }}>
                                            {tier.priceLabel}
                                          </Typography>
                                        </Box>
                                      )}
                                    </TableCell>
                                  );
                                })}
                                {renderEmptyCells(tierChunk, 3)}
                              </TableRow>
                              {/* Row 4: Configuration summary (companies/seats) */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => {
                                  const tierConfig = tierConfigs[tier.id] || {};
                                  return (
                                    <TableCell
                                      key={`config-${tier.id}`}
                                      sx={{
                                        ...cellStyle,
                                        borderLeft: "1px solid",
                                        borderRight: "1px solid",
                                        borderColor: "divider",
                                        px: 3,
                                        py: 0,
                                      }}
                                    >
                                      {tier.isContactSales && tier.priceSubLabel ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                          {tier.priceSubLabel}
                                        </Typography>
                                      ) : (tier.baseCompanies || tier.baseSeats) ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                          {tierConfig.companies || tier.baseCompanies || 0}{" "}
                                          {(tierConfig.companies || tier.baseCompanies || 0) === 1 ? "company" : "companies"}{" "}
                                          • {tierConfig.seats || tier.baseSeats || 1}{" "}
                                          {(tierConfig.seats || tier.baseSeats || 1) === 1 ? "seat" : "seats"}
                                        </Typography>
                                      ) : null}
                                    </TableCell>
                                  );
                                })}
                                {renderEmptyCells(tierChunk, 4)}
                              </TableRow>
                              {/* Row 5: Credits info */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => {
                                  const tierConfig = tierConfigs[tier.id] || {};
                                  return (
                                    <TableCell
                                      key={`credits-${tier.id}`}
                                      sx={{
                                        ...cellStyle,
                                        borderLeft: "1px solid",
                                        borderRight: "1px solid",
                                        borderColor: "divider",
                                        px: 3,
                                        py: 0,
                                      }}
                                    >
                                      {tier.isContactSales ? (
                                        (tier.creditLimit || tier.baseCreditLimit) && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                            Custom credits/month
                                          </Typography>
                                        )
                                      ) : (tier.baseCompanies || tier.baseSeats) ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                          {(
                                            (tier.baseCreditLimit || tier.creditLimit || 0) +
                                            (tierConfig.creditPackages || 0) * (tier.creditPackageIncrement || 0)
                                          ).toLocaleString()}{" "}
                                          credits/month
                                        </Typography>
                                      ) : (tier.creditLimit || tier.baseCreditLimit) ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                          {(tier.creditLimit || tier.baseCreditLimit || 0).toLocaleString()} credits/month
                                        </Typography>
                                      ) : null}
                                    </TableCell>
                                  );
                                })}
                                {renderEmptyCells(tierChunk, 5)}
                              </TableRow>
                              {/* Row 6: Total */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => {
                                  const tierConfig = tierConfigs[tier.id] || {};
                                  const calculatedPricing = getPricing(
                                    tier.id,
                                    billingPeriod,
                                    creditLevel,
                                    tier.hasConfiguration ? tierConfig.seats || 1 : 1
                                  );
                                  return (
                                    <TableCell
                                      key={`total-${tier.id}`}
                                      sx={{
                                        ...cellStyle,
                                        borderLeft: "1px solid",
                                        borderRight: "1px solid",
                                        borderColor: "divider",
                                        px: 3,
                                        py: 0,
                                        pt: 2,
                                      }}
                                    >
                                      {tier.priceFromBackend && !tier.isContactSales && (tier.hasConfiguration || tier.creditsLabel) && (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "right" }}>
                                          Total:{" "}
                                          <strong>
                                            {pricingLoading
                                              ? "Loading..."
                                              : calculatedPricing
                                              ? billingPeriod === "annual"
                                                ? `$${calculatedPricing.totalAmount}/year`
                                                : `$${calculatedPricing.totalAmount}/month`
                                              : "..."}
                                          </strong>
                                        </Typography>
                                      )}
                                    </TableCell>
                                  );
                                })}
                                {renderEmptyCells(tierChunk, 6)}
                              </TableRow>
                              {/* Row 7: Options button */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => (
                                  <TableCell
                                    key={`options-${tier.id}`}
                                    sx={{
                                      ...cellStyle,
                                      borderLeft: "1px solid",
                                      borderRight: "1px solid",
                                      borderColor: "divider",
                                      px: 3,
                                      py: 0,
                                      pt: 2,
                                    }}
                                  >
                                    {tier.hasConfiguration && (
                                      <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={(e) => handleOpenConfig(e, tier.id)}
                                        startIcon={<Tune />}
                                      >
                                        Options
                                      </Button>
                                    )}
                                  </TableCell>
                                ))}
                                {renderEmptyCells(tierChunk, 7)}
                              </TableRow>
                              {/* Row 8: Select button */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => {
                                  const isSelected = planSelection === tier.id;
                                  const isCurrentPlan = hasExistingSubscription && currentSubscriptionPlan === tier.id;
                                  return (
                                    <TableCell
                                      key={`button-${tier.id}`}
                                      sx={{
                                        ...cellStyle,
                                        borderLeft: "1px solid",
                                        borderRight: "1px solid",
                                        borderColor: "divider",
                                        px: 3,
                                        py: 0,
                                        pt: 2,
                                      }}
                                    >
                                      {tier.isContactSales ? (
                                        <Button
                                          variant="outlined"
                                          component="a"
                                          href={`mailto:${tier.salesEmail}`}
                                          fullWidth
                                        >
                                          Contact Sales
                                        </Button>
                                      ) : (
                                        <Button
                                          color={isSelected ? "primary" : "inherit"}
                                          variant={isSelected ? "contained" : "outlined"}
                                          onClick={() => {
                                            if (tier.id === "free" && hasExistingSubscription) {
                                              handleDowngradeClick(tier.id);
                                            } else {
                                              setPlanSelection(tier.id);
                                            }
                                          }}
                                          fullWidth
                                          startIcon={isSelected || isCurrentPlan ? <Check /> : null}
                                        >
                                          {isSelected ? "Selected" : isCurrentPlan ? "Current Plan" : "Select Plan"}
                                        </Button>
                                      )}
                                    </TableCell>
                                  );
                                })}
                                {renderEmptyCells(tierChunk, 8)}
                              </TableRow>
                              {/* Row 9: Divider */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => (
                                  <TableCell
                                    key={`divider-${tier.id}`}
                                    sx={{
                                      ...cellStyle,
                                      borderLeft: "1px solid",
                                      borderRight: "1px solid",
                                      borderColor: "divider",
                                      px: 3,
                                      pt: 3,
                                      pb: 0,
                                    }}
                                  >
                                    <Divider />
                                  </TableCell>
                                ))}
                                {renderEmptyCells(tierChunk, 9)}
                              </TableRow>
                              {/* Row 10: Features */}
                              <TableRow>
                                {tierChunk.map((tier: TierData) => {
                                  const tierConfig = tierConfigs[tier.id] || {};
                                  return (
                                    <TableCell
                                      key={`features-${tier.id}`}
                                      sx={{
                                        ...cellStyle,
                                        borderLeft: "1px solid",
                                        borderRight: "1px solid",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        borderBottomLeftRadius: 4,
                                        borderBottomRightRadius: 4,
                                        p: 3,
                                        pt: 3,
                                      }}
                                    >
                                      {tier.features?.map((feature: string, index: number) => {
                                        let displayFeature = feature;
                                        if (tier.hasConfiguration) {
                                          const companies = tierConfig.companies || tier.baseCompanies || 0;
                                          const seats = tierConfig.seats || tier.baseSeats || 1;
                                          const creditPackages = tierConfig.creditPackages || 0;
                                          displayFeature = displayFeature
                                            .replace("{companies}", companies.toString())
                                            .replace("{companiesLabel}", companies === 1 ? "company" : "companies")
                                            .replace("{seats}", seats.toString())
                                            .replace("{seatsLabel}", seats === 1 ? "seat" : "seats")
                                            .replace(
                                              "{totalCredits}",
                                              (
                                                (tier.baseCreditLimit || 0) +
                                                creditPackages * (tier.creditPackageIncrement || 0)
                                              ).toLocaleString()
                                            );
                                        }
                                        return (
                                          <Typography
                                            key={index}
                                            variant={index === 0 ? "body2" : "body1"}
                                            sx={{
                                              ...(index === 0
                                                ? { mb: 2, fontWeight: 500 }
                                                : { display: "flex", alignItems: "center", mb: 1 }),
                                            }}
                                          >
                                            {index > 0 && <Check sx={{ mr: 1, color: "primary.main" }} fontSize="small" />}
                                            {displayFeature}
                                          </Typography>
                                        );
                                      })}
                                    </TableCell>
                                  );
                                })}
                                {renderEmptyCells(tierChunk, 10)}
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>
                      ));
                    })()
                  )}
                </Grid>

                {/* Dynamic Configuration Popover */}
                {tierData?.tiers?.map((tier: any) => {
                  if (!tier.hasConfiguration || !tier.configuration)
                    return null;

                  return (
                    <Popover
                      key={`config-${tier.id}`}
                      open={Boolean(configAnchorEl) && configTierId === tier.id}
                      anchorEl={configAnchorEl}
                      onClose={() => {
                        setConfigAnchorEl(null);
                        setConfigTierId(null);
                      }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      PaperProps={{
                        sx: { p: 3, width: 360 },
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {tier.name} Configuration
                      </Typography>

                      {/* Render configuration options in specific order: companies, seats, credit packages */}
                      {/* Companies slider */}
                      {tier.configuration.companies &&
                        (() => {
                          const config = tier.configuration.companies;
                          const tierConfig = tierConfigs[tier.id] || {};
                          const companies =
                            tierConfig.companies || config.min || 0;
                          return (
                            <Box key="companies" sx={{ mb: 3 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Companies
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                  {companies}
                                </Typography>
                              </Box>
                              <Slider
                                value={companies}
                                onChange={(_, newValue) =>
                                  setTierConfigs((prev) => ({
                                    ...prev,
                                    [tier.id]: {
                                      ...prev[tier.id],
                                      companies: newValue as number,
                                    },
                                  }))
                                }
                                min={config.min}
                                max={config.max}
                                step={config.step}
                                marks={config.marks}
                                valueLabelDisplay="auto"
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {config.baseLabel.replace(
                                  "{baseCompanies}",
                                  tier.baseCompanies.toString()
                                )}
                                {companies > tier.baseCompanies &&
                                  config.additionalLabel.replace(
                                    "{additionalCost}",
                                    (
                                      (companies - tier.baseCompanies) *
                                      (billingPeriod === "annual" ? 16 : 20)
                                    ).toString()
                                  )}
                              </Typography>
                            </Box>
                          );
                        })()}

                      {/* Seats slider */}
                      {tier.configuration.seats &&
                        (() => {
                          const config = tier.configuration.seats;
                          const tierConfig = tierConfigs[tier.id] || {};
                          const seats = tierConfig.seats || config.min || 1;
                          return (
                            <Box key="seats" sx={{ mb: 3 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Seats
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                  {seats}
                                </Typography>
                              </Box>
                              <Slider
                                value={seats}
                                onChange={(_, newValue) =>
                                  setTierConfigs((prev) => ({
                                    ...prev,
                                    [tier.id]: {
                                      ...prev[tier.id],
                                      seats: newValue as number,
                                    },
                                  }))
                                }
                                min={config.min}
                                max={config.max}
                                step={config.step}
                                marks={config.marks}
                                valueLabelDisplay="auto"
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {config.baseLabel.replace(
                                  "{baseSeats}",
                                  tier.baseSeats.toString()
                                )}
                                {seats > tier.baseSeats &&
                                  config.additionalLabel.replace(
                                    "{additionalCost}",
                                    (
                                      (seats - tier.baseSeats) *
                                      (billingPeriod === "annual" ? 12 : 15)
                                    ).toString()
                                  )}
                              </Typography>
                            </Box>
                          );
                        })()}

                      {/* Credit packages dropdown */}
                      {tier.configuration.creditPackages &&
                        (() => {
                          const config = tier.configuration.creditPackages;
                          const tierConfig = tierConfigs[tier.id] || {};
                          const creditPackages = tierConfig.creditPackages || 0;
                          return (
                            <FormControl
                              key="creditPackages"
                              fullWidth
                              variant="outlined"
                              sx={{ mb: 2 }}
                            >
                              <InputLabel id="creditPackages-label">
                                {config.label || "Credit Packages"}
                              </InputLabel>
                              <Select
                                labelId="creditPackages-label"
                                value={creditPackages}
                                label={config.label || "Credit Packages"}
                                onChange={(e) =>
                                  setTierConfigs((prev) => ({
                                    ...prev,
                                    [tier.id]: {
                                      ...prev[tier.id],
                                      creditPackages: Number(e.target.value),
                                    },
                                  }))
                                }
                              >
                                {config.options.map((option: number) => (
                                  <MenuItem key={option} value={option}>
                                    {option === 0
                                      ? config.noneLabel || "None"
                                      : config.optionLabel.replace(
                                          "{credits}",
                                          (
                                            option * config.creditIncrement
                                          ).toString()
                                        )}
                                    {option > 0 &&
                                      config.priceLabel
                                        .replace(
                                          "{price}",
                                          billingPeriod === "annual"
                                            ? "80"
                                            : "100"
                                        )
                                        .replace(
                                          "{packages}",
                                          option.toString()
                                        )}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          );
                        })()}

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                          setConfigAnchorEl(null);
                          setConfigTierId(null);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Done
                      </Button>
                    </Popover>
                  );
                })}
              </Grid>
            </Box>
          ) : activeStep === 1 && servicePackagesChecked && hasServicePackages ? (
            <ServicePackagesStep
              getToken={getToken}
              selectedPackages={selectedServicePackages}
              onPackagesChange={setSelectedServicePackages}
              onContinue={() => getSetupIntent()}
            />
          ) : (
            <Box>
              {!stepLoading && !clientSecret && (
                <Typography>
                  Unable to initialize payment form. Please try again.
                </Typography>
              )}
              {showRedirectHandler && (
                <Elements
                  stripe={getStripeForAccount()}
                  options={{
                    appearance: {
                      theme: mode === "dark" ? "night" : "stripe",
                    },
                  }}
                >
                  <PaymentRedirectHandler
                    getToken={getToken}
                    eventListener={eventListener}
                    email={currentUser?.email || ""}
                    onSuccess={async () => {
                      console.log(
                        "[SubscriptionDialog] PaymentRedirectHandler onSuccess - payment completed"
                      );
                      await getUsage();
                      if (onSubscriptionUpdate) {
                        console.log(
                          "[SubscriptionDialog] Calling onSubscriptionUpdate after redirect payment"
                        );
                        onSubscriptionUpdate();
                      }
                      console.log(
                        "[SubscriptionDialog] Calling onClose after redirect payment"
                      );
                      onClose();
                      setShowRedirectHandler(false);
                      // Refresh the page to unlock all features
                      setTimeout(() => {
                        window.location.reload();
                      }, 1000);
                    }}
                    onError={(error) => {
                      eventListener({
                        type: "SET_ERROR",
                        error: error,
                      });
                      setShowRedirectHandler(false);
                      setActiveStep(0);
                    }}
                  />
                </Elements>
              )}
              {!stepLoading && !showRedirectHandler && clientSecret && (
                <Elements
                  stripe={stripeInstance}
                  options={{
                    clientSecret,
                    appearance: {
                      theme: mode === "dark" ? "night" : "stripe",
                    },
                  }}
                >
                  <PaymentStep
                    setModalOpen={onClose}
                    getUsage={getUsage}
                    displayName={currentUser.displayName || ""}
                    email={currentUser.email || ""}
                    getToken={getToken}
                    eventListener={eventListener}
                    skipReload={skipReloadAfterPayment}
                    productPlan={planSelection}
                    productPlanName={
                      tierData?.tiers?.find((t: any) => t.id === planSelection)
                        ?.name || planSelection
                    }
                    hasConfiguration={
                      tierData?.tiers?.find((t: any) => t.id === planSelection)
                        ?.hasConfiguration || false
                    }
                    billingPeriod={billingPeriod}
                    mode={mode}
                    seats={tierConfigs[planSelection]?.seats || 1}
                    price={(() => {
                      // Use the confirmed pricing from backend if available
                      if (confirmedPricing) {
                        return confirmedPricing.pricePerUnit;
                      }
                      // Fallback to calculated pricing from backend
                      const selectedTierConfig =
                        tierConfigs[planSelection] || {};
                      const calculatedPrice = getPricing(
                        planSelection,
                        billingPeriod,
                        planSelection === "entrepreneur"
                          ? creditLevel
                          : "standard",
                        selectedTierConfig.seats || 1
                      );
                      return calculatedPrice?.pricePerUnit || 0;
                    })()}
                    goToPreviousStep={() => setActiveStep(servicePackagesChecked && hasServicePackages ? 1 : 0)}
                    submitting={submitting}
                    creditLevel={creditLevel}
                    promoCode={promoCodeData?.valid ? promoCode : undefined}
                    companies={
                      confirmedPricing?.companies ??
                      tierConfigs[planSelection]?.companies
                    }
                    creditPackages={
                      confirmedPricing?.creditPackages ??
                      tierConfigs[planSelection]?.creditPackages
                    }
                    confirmedPricing={confirmedPricing}
                    connectedAccount={connectedAccount}
                    firstMonthSpecial={confirmedFirstMonthSpecial}
                    selectedServicePackages={availableServicePackages
                      .filter((pkg) => selectedServicePackages.includes(pkg.id))
                      .map((pkg) => ({
                        id: pkg.id,
                        name: pkg.name,
                        price: pkg.price,
                        billingType: pkg.billingType,
                        billingInterval: pkg.billingInterval,
                      }))}
                  />
                </Elements>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            justifyContent: "space-between",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          {(stepLoading || pricingLoading) && (
            <Box sx={{ width: "100%", mb: 2 }}>
              <LinearProgress />
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {activeStep === 0 ? (
              <>
                {onLogout ? (
                  <Button
                    variant="text"
                    onClick={onLogout}
                    size="large"
                  >
                    Exit
                  </Button>
                ) : (
                  <Box />
                )}
                {isDowngrading ? (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      setPendingDowngradePlan(planSelection);
                      setDowngradeConfirmOpen(true);
                    }}
                    size="large"
                    sx={{ px: 4 }}
                    disabled={stepLoading}
                  >
                    {stepLoading ? "Processing..." : "Downgrade to Free"}
                  </Button>
                ) : planSelection === "free" && !hasExistingSubscription && !requiresSubscription ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onClose()}
                    size="large"
                    sx={{ px: 4 }}
                  >
                    Close
                  </Button>
                ) : planSelection === "free" && requiresSubscription ? (
                  // When subscription is required and free is selected, show disabled button
                  <Button
                    variant="contained"
                    color="primary"
                    disabled
                    size="large"
                    sx={{ px: 4 }}
                  >
                    Please Select a Paid Plan
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      pricingLoading ||
                      (planSelection === currentSubscriptionPlan &&
                        !hasConfigurationChanged())
                    }
                    onClick={() => {
                      if (isUpdatingSubscription) {
                        handleUpdateSubscription();
                      } else if (servicePackagesChecked && hasServicePackages) {
                        // Go to service packages step
                        setActiveStep(1);
                      } else {
                        // Skip service packages, go directly to payment
                        getSetupIntent();
                      }
                    }}
                    size="large"
                    sx={{ px: 4 }}
                  >
                    {pricingLoading
                      ? "Loading Pricing..."
                      : isUpdatingSubscription
                      ? "Update Subscription"
                      : servicePackagesChecked && hasServicePackages
                      ? "Continue"
                      : "Continue to Payment"}
                  </Button>
                )}
              </>
            ) : activeStep === 1 && servicePackagesChecked && hasServicePackages ? (
              // Service packages step
              <>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(0)}
                  size="large"
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => getSetupIntent()}
                  disabled={stepLoading}
                  size="large"
                  sx={{ px: 4 }}
                >
                  {stepLoading ? (
                    "Loading..."
                  ) : selectedServicePackages.length > 0 ? (
                    "Continue to Payment"
                  ) : (
                    "Skip & Continue"
                  )}
                </Button>
              </>
            ) : (
              <>
                {!showRedirectHandler && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setActiveStep(servicePackagesChecked && hasServicePackages ? 1 : 0)
                      }
                      size="large"
                    >
                      Back
                    </Button>
                    {!stepLoading && clientSecret && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ textAlign: "right", mr: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Due Today
                            {confirmedFirstMonthSpecial?.applied && (
                              <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 0.5 }}>
                                ({billingPeriod === "annual" ? "first year" : "first month"} special)
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            $
                            {(() => {
                              // Calculate subscription price
                              // Use first month/year special price if applied
                              let subscriptionPrice = 0;
                              if (confirmedFirstMonthSpecial?.applied && confirmedPricing) {
                                if (billingPeriod === "annual") {
                                  // Annual: 11 months at regular price + first month special
                                  subscriptionPrice = (confirmedPricing.pricePerUnit * 11) + confirmedFirstMonthSpecial.price;
                                } else {
                                  // Monthly: just the first month special price
                                  subscriptionPrice = confirmedFirstMonthSpecial.price;
                                }
                              } else if (confirmedPricing) {
                                subscriptionPrice = billingPeriod === "annual"
                                  ? confirmedPricing.pricePerUnit * 12
                                  : confirmedPricing.pricePerUnit;
                              }

                              // Calculate service package totals
                              const selectedPkgs = availableServicePackages.filter(
                                (pkg) => selectedServicePackages.includes(pkg.id)
                              );
                              const oneTimeTotal = selectedPkgs
                                .filter((pkg) => pkg.billingType !== SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION)
                                .reduce((sum, pkg) => sum + pkg.price, 0);
                              const monthlyTotal = selectedPkgs
                                .filter(
                                  (pkg) =>
                                    pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION &&
                                    pkg.billingInterval === "monthly"
                                )
                                .reduce((sum, pkg) => sum + pkg.price, 0);
                              const annualTotal = selectedPkgs
                                .filter(
                                  (pkg) =>
                                    pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION &&
                                    pkg.billingInterval === "annual"
                                )
                                .reduce((sum, pkg) => sum + pkg.price, 0);

                              return (
                                subscriptionPrice + oneTimeTotal + monthlyTotal + annualTotal
                              ).toLocaleString();
                            })()}
                          </Typography>
                        </Box>
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          disabled={planSelection === "free"}
                          loading={submitting}
                          size="large"
                          sx={{ px: 4 }}
                          onClick={() => {
                            setSubmitting(true);
                          }}
                        >
                          Complete Subscription
                        </LoadingButton>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubscriptionDialog;
